import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Container,
  ContentLayout,
  CopyToClipboard,
  ExpandableSection,
  FormField,
  Grid,
  Header,
  Icon,
  Link,
  PromptInput,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import { PromptInputProps } from "@amzn/awsui-components-react/polaris/prompt-input";
import useChatUat, { ChatUatEntry } from "../../hooks/useChatUat";
import "../chat/chat.scss";
import ChatUatMarkdown from "./ChatUatMarkdown";
import "./chatUat.scss";

const WIKI_URL = "https://w.amazon.com/bin/view/AIV/FinSys/TRACK/Systems/Lusca";

const EXAMPLE_ORDER_ID = "00093f38-8cb6-43f1-b9d2-dda49ffb5de4";
const MAX_PROMPT_CHARACTERS = 4000;

const STARTER_PROMPTS = [
  {
    label: "Why did it fail?",
    prompt: `Why did order ${EXAMPLE_ORDER_ID} fail?`,
  },
  {
    label: "Compare gamma and prod amounts",
    prompt: `Compare the gamma and prod amounts for order ${EXAMPLE_ORDER_ID}.`,
  },
  {
    label: "What needs human review?",
    prompt: `What in order ${EXAMPLE_ORDER_ID} needs human review before UAT sign-off?`,
  },
];

function MessageRow({ entry }: { entry: ChatUatEntry }) {
  const isUser = entry.role === "user";
  const bubbleClass = entry.isError
    ? "chat-uat__bubble chat-uat__bubble--assistant chat-uat__bubble--error"
    : `chat-uat__bubble chat-uat__bubble--${entry.role}`;
  return (
    <div
      className={`chat-uat__row ${isUser ? "chat-uat__row--user" : ""}`}
      role="group"
      aria-label={isUser ? "Your message" : "ChatUAT message"}
    >
      <div
        className={`chat-uat__avatar chat-uat__avatar--${entry.role}`}
        aria-hidden="true"
      >
        <Icon name={isUser ? "user-profile" : "gen-ai"} />
      </div>
      <div className="chat-uat__message">
        <div
          className={bubbleClass}
          data-testid={`chat-uat-bubble-${entry.role}`}
          role={entry.isError ? "alert" : undefined}
        >
          {isUser ? entry.content : <ChatUatMarkdown text={entry.content} />}
        </div>
        {!isUser && entry.groundedIn && entry.groundedIn.length > 0 && (
          <div className="chat-uat__grounding">
            <span>Grounded in</span>
            {entry.groundedIn.map((source) => (
              <span key={source} className="chat-uat__ground-chip">
                {source}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ThinkingRow() {
  return (
    <div className="chat-uat__row">
      <div
        className="chat-uat__avatar chat-uat__avatar--assistant"
        aria-hidden="true"
      >
        <Icon name="gen-ai" />
      </div>
      <div className="chat-uat__bubble chat-uat__bubble--assistant">
        <span
          className="chat-uat__typing"
          role="status"
          aria-label="ChatUAT is thinking"
        >
          <span />
          <span />
          <span />
        </span>
      </div>
    </div>
  );
}

export default function ChatUatPage() {
  const { entries, artefact, sendMessage, reset, isLoading } = useChatUat();
  const [input, setInput] = useState("");
  const [focusRequest, setFocusRequest] = useState(0);
  const streamRef = useRef<HTMLDivElement>(null);
  const promptRef = useRef<PromptInputProps.Ref>(null);
  const previousLoading = useRef(isLoading);

  useEffect(() => {
    const justSettled = previousLoading.current && !isLoading;
    if (!isLoading && (justSettled || focusRequest > 0)) {
      promptRef.current?.focus();
    }
    previousLoading.current = isLoading;
  }, [focusRequest, isLoading]);

  useEffect(() => {
    const el = streamRef.current;
    if (el && typeof el.scrollTo === "function") {
      el.scrollTo({ top: el.scrollHeight });
    }
  }, [entries, isLoading]);

  const promptTooLong = Array.from(input).length > MAX_PROMPT_CHARACTERS;

  const submit = () => {
    if (isLoading || promptTooLong || !input.trim()) {
      return;
    }
    sendMessage(input);
    setInput("");
  };

  const startNewConversation = () => {
    reset();
    setInput("");
    setFocusRequest((request) => request + 1);
  };

  return (
    <ContentLayout
      defaultPadding
      header={
        <Header
          variant="h1"
          info={
            <Link href={WIKI_URL} variant="info">
              Info
            </Link>
          }
          description="Ask why a shadow-test order failed and get a review you can circulate for sign-off."
          actions={
            <Button
              onClick={startNewConversation}
              disabled={entries.length === 0 && !input}
            >
              New conversation
            </Button>
          }
        >
          ChatUAT
        </Header>
      }
    >
      <Grid
        gridDefinition={[
          { colspan: { default: 12, m: 7 } },
          { colspan: { default: 12, m: 5 } },
        ]}
      >
        <Container
          footer={
            <FormField
              errorText={
                promptTooLong
                  ? "Questions are limited to 4,000 characters."
                  : undefined
              }
            >
              <PromptInput
                ref={promptRef}
                value={input}
                onChange={({ detail }) => setInput(detail.value)}
                onAction={submit}
                ariaLabel="Ask about a shadow-test order"
                disabled={isLoading}
                disableActionButton={
                  isLoading || !input.trim() || promptTooLong
                }
                disableBrowserAutocorrect
                spellcheck={false}
                actionButtonIconName="arrow-up"
                actionButtonAriaLabel="Send message"
                placeholder="Ask about a shadow-test order"
                maxRows={4}
                data-testid="chat-uat-input"
              />
            </FormField>
          }
        >
          <div
            className="chat-uat__thread"
            ref={streamRef}
            role="log"
            aria-label="ChatUAT conversation"
            aria-live="polite"
            aria-relevant="additions"
            aria-busy={isLoading}
          >
            {entries.length === 0 && !isLoading && (
              <div className="chat-uat__empty">
                <SpaceBetween size="s">
                  <Box color="text-body-secondary">
                    Ask about a failing shadow-test order to get started.
                  </Box>
                  <div className="chat-uat__starters">
                    {STARTER_PROMPTS.map(({ label, prompt }) => (
                      <Button
                        key={label}
                        onClick={() => sendMessage(prompt)}
                        disabled={isLoading}
                      >
                        {label}
                      </Button>
                    ))}
                  </div>
                  <Box fontSize="body-s" color="text-body-secondary">
                    Example order{" "}
                    <CopyToClipboard
                      variant="inline"
                      textToCopy={EXAMPLE_ORDER_ID}
                      copyButtonAriaLabel="Copy the example order id"
                      copySuccessText="Order id copied"
                      copyErrorText="Order id failed to copy"
                    />
                  </Box>
                </SpaceBetween>
              </div>
            )}
            {entries.map((entry) => (
              <MessageRow key={entry.id} entry={entry} />
            ))}
            {isLoading && <ThinkingRow />}
          </div>
        </Container>

        <Container
          header={
            <Header
              variant="h2"
              actions={
                <CopyToClipboard
                  copyButtonText="Copy for sign-off"
                  copyButtonAriaLabel="Copy the review for sign-off"
                  copySuccessText="Review copied"
                  copyErrorText="Review failed to copy"
                  textToCopy={artefact?.raw ?? ""}
                  disabled={!artefact}
                  disabledReason={artefact ? undefined : "No review yet."}
                />
              }
            >
              Shareable review
            </Header>
          }
        >
          {artefact ? (
            <SpaceBetween size="m">
              <div
                className="chat-uat__artefact"
                data-testid="chat-uat-artefact"
              >
                {artefact.primary}
              </div>
              {artefact.details && (
                <ExpandableSection
                  variant="footer"
                  headerText="Coverage and provenance"
                >
                  <div className="chat-uat__artefact">{artefact.details}</div>
                </ExpandableSection>
              )}
            </SpaceBetween>
          ) : (
            <Box color="text-body-secondary">
              The shareable review appears here once ChatUAT has analysed an
              order.
            </Box>
          )}
        </Container>
      </Grid>
    </ContentLayout>
  );
}
